/* @flow */

import type { NETGEM_API_V8_DEEPLINK_RESPONSE } from '../../../../libs/netgemLibrary/v8/types/Deeplink';
import type { NETGEM_API_V8_URL_DEFINITION } from '../../../../libs/netgemLibrary/v8/types/NtgVideoFeed';
import type { NETGEM_API_V8_REQUEST_RESPONSE } from '../../../../libs/netgemLibrary/v8/types/RequestResponse';
import type { CombinedReducers } from '../../../reducers';
import type { Dispatch } from '../../../types/types';
import type { NetgemApiEmitterType, RequestResponseMethodDefinitionType } from '../emitter';

import { createCustomNetworkErrorFromKey } from '../../../../libs/netgemLibrary/helpers/CreateCustomNetworkError';
import { ItemType } from '../../../../libs/netgemLibrary/v8/types/FeedItem';
import { REDUX_MSG_REQUEST_GENERIC } from '../../constants';
import { generateApiUrl } from '../helpers/api';

const sendV8AssetIdForDeeplinkRequestV1: (
  type: ItemType,
  cncProgramGetUrl: ?NETGEM_API_V8_URL_DEFINITION,
  cncSeriesGetUrl: ?NETGEM_API_V8_URL_DEFINITION,
  cncParameter: string,
  state: CombinedReducers,
  NetgemApiEmitter: NetgemApiEmitterType,
  signal?: AbortSignal,
) => Promise<NETGEM_API_V8_REQUEST_RESPONSE> = (type, cncProgramGetUrl, cncSeriesGetUrl, cncParameter, state, NetgemApiEmitter, signal) => {
  const urlDef = type === ItemType.Program ? cncProgramGetUrl : cncSeriesGetUrl;

  if (!urlDef) {
    return Promise.reject(createCustomNetworkErrorFromKey('common.messages.errors.missing_url_definition', { urlDef: type === ItemType.Program ? 'cncProgramGetUrl' : 'cncSeriesGetUrl' }));
  }

  const { authent, method } = urlDef;

  return NetgemApiEmitter.emit(REDUX_MSG_REQUEST_GENERIC, {
    authent,
    method,
    signal,
    state,
    uri: generateApiUrl(urlDef, { cncParameter }, state),
  });
};

const sendV8AssetIdForDeeplinkRequestV2: (
  type: ItemType,
  deeplinkProgramGetUrl: NETGEM_API_V8_URL_DEFINITION,
  deeplinkSeriesGetUrl: NETGEM_API_V8_URL_DEFINITION,
  deeplinkParameter: string,
  state: CombinedReducers,
  NetgemApiEmitter: NetgemApiEmitterType,
  signal?: AbortSignal,
) => Promise<NETGEM_API_V8_REQUEST_RESPONSE> = (type, deeplinkProgramGetUrl, deeplinkSeriesGetUrl, deeplinkParameter, state, NetgemApiEmitter, signal) => {
  const urlDef = type === ItemType.Program ? deeplinkProgramGetUrl : deeplinkSeriesGetUrl;

  if (!urlDef) {
    return Promise.reject(createCustomNetworkErrorFromKey('common.messages.errors.missing_url_definition', { urlDef: type === ItemType.Program ? 'deeplinkProgramGetUrl' : 'deeplinkSeriesGetUrl' }));
  }

  const { authent, method } = urlDef;

  return NetgemApiEmitter.emit(REDUX_MSG_REQUEST_GENERIC, {
    authent,
    method,
    signal,
    state,
    uri: generateApiUrl(urlDef, { deeplinkParameter }, state),
  });
};

const sendV8AssetIdForDeeplinkRequest: (type: ItemType, deeplinkParameter: string, signal?: AbortSignal) => RequestResponseMethodDefinitionType =
  (type, deeplinkParameter, signal) =>
  (dispatch: Dispatch, getState: () => CombinedReducers, NetgemApiEmitter: NetgemApiEmitterType): Promise<NETGEM_API_V8_DEEPLINK_RESPONSE> => {
    const state = getState();
    const {
      netgemApi: { cncProgramGetUrl, cncSeriesGetUrl, deeplinkProgramGetUrl, deeplinkSeriesGetUrl },
    } = state;

    // Backward compatibility: deeplink > CNC
    const promise =
      deeplinkProgramGetUrl && deeplinkSeriesGetUrl
        ? sendV8AssetIdForDeeplinkRequestV2(type, deeplinkProgramGetUrl, deeplinkSeriesGetUrl, deeplinkParameter, state, NetgemApiEmitter, signal)
        : sendV8AssetIdForDeeplinkRequestV1(type, cncProgramGetUrl, cncSeriesGetUrl, deeplinkParameter, state, NetgemApiEmitter, signal);

    return promise.then(({ result: { id, kind } }) => {
      return { id, kind };
    });
  };

export default sendV8AssetIdForDeeplinkRequest;
